import { useState, useCallback } from 'react';
import { BarcodeScanner, BarcodeFormat, Barcode } from '@capacitor-mlkit/barcode-scanning';
import { Capacitor } from '@capacitor/core';

export interface BarcodeScanResult {
  success: boolean;
  barcode?: Barcode;
  error?: string;
}

/**
 * Hook for barcode/QR code scanning using ML Kit
 * Supports all major barcode formats including UPC, EAN, QR codes
 */
export const useBarcodeScanner = () => {
  const [isScanning, setIsScanning] = useState(false);
  const [isSupported, setIsSupported] = useState(true);

  /**
   * Check if barcode scanning is supported on this device
   */
  const checkSupport = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      setIsSupported(false);
      return false;
    }

    try {
      const { supported } = await BarcodeScanner.isSupported();
      setIsSupported(supported);
      return supported;
    } catch (error) {
      console.error('[BarcodeScanner] Support check failed:', error);
      setIsSupported(false);
      return false;
    }
  }, []);

  /**
   * Request camera permissions
   */
  const requestPermissions = useCallback(async (): Promise<boolean> => {
    try {
      const { camera } = await BarcodeScanner.requestPermissions();
      return camera === 'granted' || camera === 'limited';
    } catch (error) {
      console.error('[BarcodeScanner] Permission request failed:', error);
      return false;
    }
  }, []);

  /**
   * Check if camera permissions are granted
   */
  const checkPermissions = useCallback(async (): Promise<boolean> => {
    try {
      const { camera } = await BarcodeScanner.checkPermissions();
      return camera === 'granted' || camera === 'limited';
    } catch (error) {
      console.error('[BarcodeScanner] Permission check failed:', error);
      return false;
    }
  }, []);

  /**
   * Start scanning for barcodes
   * Returns a promise that resolves when a barcode is detected
   */
  const startScan = useCallback(async (
    formats?: BarcodeFormat[]
  ): Promise<BarcodeScanResult> => {
    try {
      // Check support
      const supported = await checkSupport();
      if (!supported) {
        return {
          success: false,
          error: 'Barcode scanning is not supported on this device'
        };
      }

      // Check/request permissions
      const hasPermission = await checkPermissions();
      if (!hasPermission) {
        const granted = await requestPermissions();
        if (!granted) {
          return {
            success: false,
            error: 'Camera permission denied'
          };
        }
      }

      setIsScanning(true);

      // Use the scan() method which returns the scanned barcode
      const result = await BarcodeScanner.scan({
        formats: formats || [
          BarcodeFormat.QrCode,
          BarcodeFormat.Ean13,
          BarcodeFormat.Ean8,
          BarcodeFormat.UpcA,
          BarcodeFormat.UpcE,
          BarcodeFormat.Code128,
          BarcodeFormat.Code39,
          BarcodeFormat.Code93,
        ]
      });

      setIsScanning(false);

      if (result.barcodes && result.barcodes.length > 0) {
        console.log('[BarcodeScanner] Barcode detected:', result.barcodes[0]);
        return {
          success: true,
          barcode: result.barcodes[0]
        };
      } else {
        return {
          success: false,
          error: 'No barcode detected'
        };
      }

    } catch (error: any) {
      console.error('[BarcodeScanner] Scan failed:', error);
      setIsScanning(false);
      return {
        success: false,
        error: error.message || 'Failed to scan barcode'
      };
    }
  }, [checkSupport, checkPermissions, requestPermissions]);

  /**
   * Scan a single barcode and return immediately
   * This is a simpler API that handles the full flow
   */
  const scanSingle = useCallback(async (
    formats?: BarcodeFormat[]
  ): Promise<BarcodeScanResult> => {
    try {
      // Check support
      const supported = await checkSupport();
      if (!supported) {
        return {
          success: false,
          error: 'Barcode scanning is not supported on this device'
        };
      }

      // Check/request permissions
      let hasPermission = await checkPermissions();
      if (!hasPermission) {
        hasPermission = await requestPermissions();
        if (!hasPermission) {
          return {
            success: false,
            error: 'Camera permission denied'
          };
        }
      }

      setIsScanning(true);

      // Scan single barcode
      const result = await BarcodeScanner.scan({
        formats: formats || [
          BarcodeFormat.QrCode,
          BarcodeFormat.Ean13,
          BarcodeFormat.Ean8,
          BarcodeFormat.UpcA,
          BarcodeFormat.UpcE,
          BarcodeFormat.Code128,
          BarcodeFormat.Code39,
          BarcodeFormat.Code93,
        ]
      });

      setIsScanning(false);

      if (result.barcodes && result.barcodes.length > 0) {
        return {
          success: true,
          barcode: result.barcodes[0]
        };
      } else {
        return {
          success: false,
          error: 'No barcode detected'
        };
      }

    } catch (error: any) {
      console.error('[BarcodeScanner] Scan failed:', error);
      setIsScanning(false);
      return {
        success: false,
        error: error.message || 'Failed to scan barcode'
      };
    }
  }, [checkSupport, checkPermissions, requestPermissions]);

  /**
   * Stop scanning
   */
  const stopScan = useCallback(async () => {
    try {
      await BarcodeScanner.stopScan();
      setIsScanning(false);
    } catch (error) {
      console.error('[BarcodeScanner] Stop scan failed:', error);
    }
  }, []);

  return {
    isScanning,
    isSupported,
    checkSupport,
    requestPermissions,
    checkPermissions,
    startScan,
    scanSingle,
    stopScan
  };
};
